/**
 * The generic ESC/POS adapter: the Electron main-process driver
 * (`electron/main/services/escpos-lan`) behind the shared `PrinterAdapter`
 * contract.
 *
 * The lifecycle owner is main. This adapter holds no socket and no timer: it
 * hands main the saved endpoint and profile on every call, so a job accepted
 * against one saved printer can never be rerouted by a later settings change,
 * and it passes main's job outcomes through unchanged.
 *
 * The backend is resolved from the capability table, lazily, and a rejection
 * is not cached — a register whose shell bridge was not ready must be able to
 * try again. On a runtime without `escPosLanPrinting` the resolution throws a
 * typed `PlatformCapabilityUnsupportedError`; there is no mock to fall to.
 */
import { getPlatformPrinter } from 'src/platform/services';
import type {
  EscPosJobResult,
  EscPosLanBackend,
  EscPosPrinterStatus,
  EscPosDiscoverRequest,
  EscPosDiscoveryProgress,
  EscPosConnectionDescriptor,
} from 'src/platform/services';

import { resolveEscPosProfileForDisplay } from '../escpos-profile';
import {
  isEscPosPrinter,
  escPosTransportOf,
  type SavedPrinter,
  type PrinterStatus,
  type PrinterAdapter,
  type PrintJobResult,
  type SavedEscPosPrinter,
  type EscPosLanConnection,
  type PrinterCapabilities,
  type PrinterStatusListener,
  type EscPosSerialConnection,
  type EscPosWindowsRawConnection,
} from '../printer-types';

let backendPromise: Promise<EscPosLanBackend> | null = null;

/** Resolved once, cached as the promise so concurrent callers share it; a rejection is not cached. */
export function escPosBackend(): Promise<EscPosLanBackend> {
  if (backendPromise !== null) return backendPromise;
  const pending = getPlatformPrinter().resolveEscPosBackend();
  backendPromise = pending;
  pending.catch(() => {
    if (backendPromise === pending) backendPromise = null;
  });
  return pending;
}

/** @internal test seam */
export function __resetEscPosBackendForTests(): void {
  backendPromise = null;
}

function requireEscPos(printer: SavedPrinter): SavedEscPosPrinter {
  if (!isEscPosPrinter(printer)) throw new Error('Not an ESC/POS printer.');
  return printer;
}

/** The descriptor main needs on every call: how to reach the printer, its name and the saved profile. */
export function toDescriptor(printer: SavedEscPosPrinter): EscPosConnectionDescriptor {
  const common = {
    identifier: printer.identifier,
    name: printer.name,
    profile: printer.profile as Record<string, unknown>,
  };
  const { connection } = printer;
  switch (escPosTransportOf(printer)) {
    case 'windows-raw': {
      const raw = connection as EscPosWindowsRawConnection;
      return {
        transport: 'windows-raw',
        queueName: raw.queueName,
        driverName: raw.driverName,
        portName: raw.portName,
        pnpDeviceId: raw.pnpDeviceId,
        usb: raw.usb,
        ...common,
      };
    }
    case 'serial': {
      const serial = connection as EscPosSerialConnection;
      return {
        transport: 'serial',
        portName: serial.portName,
        baudRate: serial.baudRate,
        friendlyName: serial.friendlyName,
        pnpDeviceId: serial.pnpDeviceId,
        usb: serial.usb,
        ...common,
      };
    }
    case 'lan':
    default: {
      const lan = connection as EscPosLanConnection;
      return { transport: 'lan', ipAddress: lan.ipAddress, port: lan.port, ...common };
    }
  }
}

export function toPrinterStatus(status: EscPosPrinterStatus): PrinterStatus {
  return {
    connected: status.connected,
    online: status.online,
    coverOpen: status.coverOpen,
    paperEmpty: status.paperEmpty,
    paperNearEmpty: status.paperNearEmpty,
    cutterError: status.cutterError,
    message: status.message,
    updatedAt: status.updatedAt,
    statusReporting: status.statusReporting,
  };
}

export function toJobResult(printerId: string, result: EscPosJobResult): PrintJobResult {
  return {
    success: result.success,
    outcome: result.outcome,
    acceptedBy: result.acceptedBy,
    jobId: result.jobId,
    printerId,
    status: result.status ? toPrinterStatus(result.status) : undefined,
    message: result.message,
    error: result.error,
  };
}

export const escPosPrinterAdapter: PrinterAdapter = {
  driver: 'escpos',

  capabilities(printer: SavedPrinter): PrinterCapabilities {
    const escpos = requireEscPos(printer);
    const profile = resolveEscPosProfileForDisplay(escpos.profile);
    const transport = escPosTransportOf(escpos);
    let statusReporting: PrinterCapabilities['statusReporting'];
    if (transport === 'windows-raw') statusReporting = 'limited';
    else statusReporting = profile.statusQueries ? 'unknown' : 'unsupported';
    return {
      driver: 'escpos',
      transports: [transport],
      statusReporting,
      rasterImages: true,
      cutter: profile.cutter !== 'none',
      cashDrawer: profile.drawer.enabled,
    };
  },

  async ensureConnected(printer: SavedPrinter): Promise<void> {
    const backend = await escPosBackend();
    const result = await backend.connect(toDescriptor(requireEscPos(printer)));
    if (!result.success) {
      throw Object.assign(new Error(result.error?.message ?? 'Could not connect to the printer.'), {
        code: result.error?.code ?? 'CONNECTION_FAILED',
      });
    }
  },

  async disconnect(printer: SavedPrinter): Promise<void> {
    const backend = await escPosBackend();
    await backend.disconnect({ identifier: requireEscPos(printer).identifier });
  },

  async getStatus(printer: SavedPrinter, options): Promise<PrinterStatus> {
    const backend = await escPosBackend();
    return toPrinterStatus(
      await backend.getStatus(
        { connection: toDescriptor(requireEscPos(printer)) },
        { forceReconnect: options.forceReconnect }
      )
    );
  },

  async printImages(printer, images, options): Promise<PrintJobResult> {
    const backend = await escPosBackend();
    const target = { connection: toDescriptor(requireEscPos(printer)) };
    // The job is dispatched to the main process in one call; this is the last
    // point at which a departed customer can still stop it.
    if (options.isCurrent && !options.isCurrent()) {
      return {
        success: false,
        cancelled: true,
        outcome: 'not-sent',
        jobId: options.jobId,
        printerId: printer.identifier,
      };
    }
    const result =
      images.length === 1
        ? await backend.printImage({
            ...target,
            imageBase64: images[0] ?? '',
            cutPaper: true,
            openCashDrawer: options.openCashDrawer,
            jobId: options.jobId,
          })
        : await backend.printReceipt({
            ...target,
            items: images.flatMap((imageBase64, index) =>
              index === images.length - 1
                ? [{ type: 'image' as const, imageBase64 }]
                : [{ type: 'image' as const, imageBase64 }, { type: 'cut' as const }]
            ),
            cutPaper: true,
            openCashDrawer: options.openCashDrawer,
            jobId: options.jobId,
          });
    return toJobResult(printer.identifier, result);
  },

  async openCashDrawer(printer, options): Promise<PrintJobResult> {
    const backend = await escPosBackend();
    const result = await backend.openCashDrawer({
      connection: toDescriptor(requireEscPos(printer)),
      jobId: options.jobId,
    });
    return toJobResult(printer.identifier, result);
  },

  subscribeStatus(listener: PrinterStatusListener): () => void {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;
    escPosBackend()
      .then((backend) => {
        if (cancelled) return;
        unsubscribe = backend.onStatus((event) => {
          if (!cancelled) listener(event.identifier, toPrinterStatus(event.status));
        });
      })
      .catch(() => {
        // No ESC/POS backend on this runtime: nothing to subscribe to.
      });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  },
};

/**
 * Setup-time operations that are not part of the per-printer lifecycle:
 * discovery and testing a candidate that is not saved yet.
 */
export const escPosSetup = {
  discover(request: EscPosDiscoverRequest) {
    return escPosBackend().then((backend) => backend.discover(request));
  },
  cancelDiscovery(scanId: string) {
    return escPosBackend().then((backend) => backend.cancelDiscovery(scanId));
  },
  listInterfaces(port: number) {
    return escPosBackend().then((backend) => backend.listInterfaces(port));
  },
  /** Installed Windows print queues and COM ports; `supported: false` where there is no OS bridge. */
  listDevices() {
    return escPosBackend().then((backend) => backend.listDevices());
  },
  onDiscoveryProgress(listener: (event: EscPosDiscoveryProgress) => void): () => void {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;
    escPosBackend()
      .then((backend) => {
        if (cancelled) return;
        unsubscribe = backend.onDiscoveryProgress(listener);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  },
  /** Connects to a candidate and reads what it says about itself. */
  async testConnection(printer: SavedEscPosPrinter): Promise<{ status: PrinterStatus; connectionId?: string }> {
    const backend = await escPosBackend();
    const result = await backend.connect(toDescriptor(printer));
    if (!result.success || !result.status) {
      throw Object.assign(new Error(result.error?.message ?? 'The printer did not answer.'), {
        code: result.error?.code ?? 'CONNECTION_FAILED',
      });
    }
    return { status: toPrinterStatus(result.status), connectionId: result.connectionId };
  },
  /** Prints a bitmap on a candidate — explicitly, on that endpoint only. */
  async printTestImageTo(printer: SavedEscPosPrinter, imageBase64: string, jobId: string): Promise<PrintJobResult> {
    const backend = await escPosBackend();
    return toJobResult(
      printer.identifier,
      await backend.printImage({ connection: toDescriptor(printer), imageBase64, cutPaper: true, jobId })
    );
  },
  /** Releases a candidate's connection that was never saved. */
  async release(printer: SavedEscPosPrinter): Promise<void> {
    const backend = await escPosBackend();
    await backend.disconnect({ identifier: printer.identifier }).catch(() => undefined);
  },
};
